import {every, isEmpty} from 'lodash';
import {Fragment, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {billingPlatformApi, userApi} from 'api';
import {PageHeader, DataWrapper, Card, AccountPlanBanner} from 'components';
import {VALID_PAYMENT_SOURCE_STATUSES} from 'constants/billing-platform';
import {ACCOUNT_PLAN_NAME_TRANSLATE} from 'constants/translate';
import {BillingPlatformSubscriptionStatus} from 'enums';
import {useApiData, useObservable} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {accountPlanStore} from 'services';
import {billingPlatformUtils} from 'utils';

import {BillingAddressSection} from './BillingAddressSection';
import {Invoices} from './Invoices';
import {MaxUsersChart} from './MaxUsersChart';
import {PaymentSources} from './PaymentSources';
import {SubscriptionInfo, SubscriptionStatus} from './SubscriptionInfo';
import {Transactions} from './Transactions';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING;

export const Billing: React.FC = () => {
    const {t} = useTranslation();
    const {planId, hasBillingPlatformProfile} = useObservable(accountPlanStore);
    const isPlanWithBilling = useMemo(() => billingPlatformUtils.isPlanWithBilling(planId), [planId]);
    const isPlanWithBillingPlatformPayment = useMemo(() => billingPlatformUtils.isPlanWithBillingPlatformPayment(planId), [planId]);

    const {
        send: fetchPlatformData,
        data: platformData,
        loading: fetchPlatformDataLoading,
        reset
    } = useApiData(billingPlatformApi.getBillingPlatformData);
    const {send: fetchUsers, data: {count} = {count: 0}} = useApiData(userApi.getUsersCount);

    const noValidPaymentSources = useMemo(() =>
        every(platformData?.paymentSources, ({status}) => !VALID_PAYMENT_SOURCE_STATUSES.includes(status)), [platformData]);

    useEffect(() => {
        if (hasBillingPlatformProfile) {
            fetchPlatformData();
            fetchUsers();
        }

        return () => {
            reset();
        };
    }, [hasBillingPlatformProfile, fetchPlatformData, fetchUsers, reset]);

    return (
        <Fragment>
            <PageHeader
                title={t(T_KEYS.TITLE)}
                description={t(T_KEYS.DESCRIPTION)}
            />

            <DataWrapper isLoading={!planId || fetchPlatformDataLoading}>
                {hasBillingPlatformProfile ?
                    <Fragment>
                        {!isEmpty(platformData?.subscription) &&
                            <SubscriptionInfo
                                userCount={count}
                                hasPaymentWarning={noValidPaymentSources}
                                subscription={platformData.subscription}
                                subscriptionNextPeriod={platformData.subscriptionNextPeriod || platformData.subscription}
                                onChange={fetchPlatformData}
                            />}
                        {isPlanWithBilling &&
                            <Fragment>
                                {!isEmpty(platformData?.customer?.billingAddress) &&
                                    <BillingAddressSection
                                        billingAddress={platformData.customer.billingAddress}
                                        onChange={fetchPlatformData}
                                    />}
                                <PaymentSources
                                    hasWarning={noValidPaymentSources}
                                    paymentSources={platformData?.paymentSources}
                                    primaryPaymentSourceId={platformData?.customer?.primaryPaymentSourceId}
                                    onChange={fetchPlatformData}
                                />
                                {isPlanWithBillingPlatformPayment &&
                                    <Fragment>
                                        <Invoices invoices={platformData?.invoices} />
                                        <Transactions transactions={platformData?.transactions} />
                                        <MaxUsersChart />
                                    </Fragment>}
                            </Fragment>}
                        <AccountPlanBanner className='tw-mt-10' />
                    </Fragment> :
                    <Card>
                        <div className='tw-ts-h5-bold -md:tw-ts-h5-bold tw-flex tw-items-center'>
                            <div className='tw-mr-4'>{t(ACCOUNT_PLAN_NAME_TRANSLATE[planId])}</div>
                            <SubscriptionStatus status={BillingPlatformSubscriptionStatus.Active} />
                        </div>
                    </Card>}
            </DataWrapper>
        </Fragment>
    );
};